import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import NumberInput from "../common/NumberInput";
import { EditableWaypointCardProps } from "../../types/screensTypes";
import { saveTemporaryWaypoint } from "../../api";
import { ReactComponent as EditSvg } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as DeleteBinSvg } from "../../assets/images/delete-bin-line.svg";
import { Checkbox, Slider, Tooltip } from "@mui/material";
import { useTrackCreation } from "../../context/TrackCreationContext";
import { SaveTemporaryWaypointParams } from "../../types/requestInputs";
import { useNotification } from "../../context/NotificationContext";
import { useGlobalState } from "../../context/useGlobalState";
import wpStyles from "./EditableWaypointCard.module.css";

const EditableWaypointCard: React.FC<EditableWaypointCardProps> = ({
    name,
    position,
    onClick,
    onDelete,
    dragHandleProps,
    color = "white",
    isLoading = false,
    waypoint,
    onSaveStart,
    onSaveEnd,
    onUnsavedChange,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { trackData } = useTrackCreation();
    const {
        data: { filtersAI },
    } = useGlobalState();

    const { showNotification } = useNotification();
    const waypointType = waypoint?.type;

    // Check that a temporary track subType or a track filterId can have only garden stories (case for cruise and train for example)
    const hasOnlyGardenStories = useMemo(() => {
        if (!trackData || !filtersAI || !Array.isArray(filtersAI)) return false;

        const type = trackData.filterId ?? trackData.subType;

        const matchingFilter = filtersAI.find((filter) => filter.value === type);
        return matchingFilter?.hasOnlyGardenStories === true;
    }, [trackData, filtersAI]);

    useEffect(() => {
        if (hasOnlyGardenStories) {
            setIsInGarden(true);
        }
    }, [hasOnlyGardenStories]);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    const [isInLoop, setIsInLoop] = useState<boolean>(waypoint?.inLoop === true);
    const [startDistance, setStartDistance] = useState<number>(waypoint?.startDistance || 20);
    const [isReferenceStopForReturnLoopWpt, setIsReferenceStopForReturnLoopWpt] = useState<boolean>(
        waypoint?.isReferenceStopForReturnLoop === true,
    );
    const [isStraightLine, setIsStraightLine] = useState<boolean>(waypoint?.isStraightLine === true);
    const [isInGarden, setIsInGarden] = useState<boolean>(waypoint?.type === "GARDEN" || hasOnlyGardenStories);

    // Compute a stable internalId that matches what the backend will create
    // Backend uses: `${waypointId}-temp-${languageForTemp}` where languageForTemp defaults to "iduniqueen"
    // This ensures we always use the same ID for the same waypoint, avoiding race conditions
    // and ensuring consistency even if the component re-mounts
    const stableInternalId = useMemo(() => {
        // If waypoint already has an internalId (from a temporary waypoint), use that
        if (waypoint?.internalId) {
            return waypoint.internalId;
        }
        // Otherwise, compute the same ID that the backend would create
        return `${waypoint.id}-temp-iduniqueen`;
    }, [waypoint?.id, waypoint?.internalId]);

    const saveWaypoint = useCallback(
        async (params: Partial<SaveTemporaryWaypointParams>): Promise<void> => {
            const token = localStorage.getItem("token");
            const trackId = trackData.trackId;
            if (!token || !waypoint.id || !trackId) return;

            // Notify parent that a save is starting (to block drag and drop)
            onSaveStart?.();

            const payload: SaveTemporaryWaypointParams = {
                token,
                trackId,
                waypointId: waypoint.id,
                internalId: stableInternalId,
                ...params,
            };

            try {
                await saveTemporaryWaypoint(payload);
                // Notify parent that there are unsaved changes (lightweight, no fetch)
                onUnsavedChange?.();
            } finally {
                // Notify parent that save is complete (even if it failed)
                onSaveEnd?.();
            }
        },
        [trackData.trackId, waypoint.id, stableInternalId, onSaveStart, onSaveEnd, onUnsavedChange],
    );

    return (
        <div className={wpStyles.card} style={{ background: color }}>
            <div className={wpStyles.header} style={{ background: color }}>
                <div className={wpStyles.dragHandle} {...dragHandleProps}>
                    <span>::</span>
                </div>
                <div
                    className={wpStyles.content}
                    onClick={(e) => {
                        e.preventDefault();
                        onClick();
                    }}
                >
                    <div>
                        <p className={wpStyles.position}>
                            {t("positionWithSemicolon")} {position}
                        </p>
                    </div>
                </div>
            </div>

            <Tooltip title={`${t("edit")}: ${name}`} placement="bottom-start">
                <div className={wpStyles.title}>
                    <p className={wpStyles.name}>{name}</p>
                </div>
            </Tooltip>

            <div className={wpStyles.settingGroup}>
                <p className={wpStyles.radiusLabel}>{t("startingDistance")}</p>
                <div className={wpStyles.settingStartDistance} style={{ marginTop: "8px" }}>
                    <NumberInput
                        placeholder={t("selectNumber")}
                        value={startDistance}
                        size={"small"}
                        min={1}
                        onChange={(e) => {
                            const raw = e.target.value;
                            const value = parseInt(raw || "1", 10);
                            if (isNaN(value)) return;

                            setStartDistance(value);

                            if (debounceRef.current) {
                                clearTimeout(debounceRef.current);
                            }

                            debounceRef.current = setTimeout(async () => {
                                try {
                                    await saveWaypoint({ startDistance: value });
                                } catch {
                                    showNotification({ message: t("waypointSaveFailed"), type: "error" });
                                }
                            }, 400);
                        }}
                    />
                    <Slider
                        value={startDistance}
                        min={1}
                        max={800}
                        step={1}
                        onChange={(_, value) => {
                            if (typeof value !== "number") return;
                            setStartDistance(value);
                        }}
                        onChangeCommitted={(_, value) => {
                            if (typeof value !== "number") return;

                            if (debounceRef.current) {
                                clearTimeout(debounceRef.current);
                            }

                            debounceRef.current = setTimeout(async () => {
                                try {
                                    await saveWaypoint({ startDistance: value });
                                } catch {
                                    showNotification({ message: t("waypointSaveFailed"), type: "error" });
                                }
                            }, 400);
                        }}
                        sx={{
                            width: 150,
                            "& .MuiSlider-thumb": { backgroundColor: "#000" },
                            "& .MuiSlider-track": { border: "none", backgroundColor: "#000" },
                            "& .MuiSlider-rail": { backgroundColor: "#000" },
                        }}
                    />
                </div>
            </div>

            <div className={wpStyles.settingsContainer}>
                <div className={wpStyles.settings}>
                    <div className={wpStyles.settingCheckbox}>
                        <Tooltip title={isReferenceStopForReturnLoopWpt ? t("disabledIsInLoop") : t("isInLoop")}>
                            <span>
                                <Checkbox
                                    checked={isInLoop}
                                    onChange={async (_, checked) => {
                                        const prev = isInLoop;
                                        setIsInLoop(checked);
                                        try {
                                            await saveWaypoint({ inLoop: checked });
                                        } catch {
                                            setIsInLoop(prev);
                                            showNotification({ message: t("waypointSaveFailed"), type: "error" });
                                        }
                                    }}
                                    disabled={isReferenceStopForReturnLoopWpt}
                                />
                            </span>
                        </Tooltip>
                        <p>{t("loop")}</p>
                    </div>

                    <div className={wpStyles.settingCheckbox}>
                        <Tooltip title={t("isEndOfSection")}>
                            <Checkbox
                                checked={isReferenceStopForReturnLoopWpt}
                                onChange={async (_, checked) => {
                                    const prevIsLast = isReferenceStopForReturnLoopWpt;
                                    const prevInLoop = isInLoop;
                                    setIsReferenceStopForReturnLoopWpt(checked);

                                    // When checking isLast, we also need to set inLoop=true
                                    // Combine both updates into a single API call to avoid race conditions
                                    const shouldSetInLoop = checked && !isInLoop;
                                    if (shouldSetInLoop) {
                                        setIsInLoop(true);
                                    }

                                    try {
                                        await saveWaypoint({
                                            isReferenceStopForReturnLoop: checked,
                                            ...(shouldSetInLoop && { inLoop: true }),
                                        });
                                    } catch {
                                        setIsReferenceStopForReturnLoopWpt(prevIsLast);
                                        if (shouldSetInLoop) {
                                            setIsInLoop(prevInLoop);
                                        }
                                        showNotification({ message: t("waypointSaveFailed"), type: "error" });
                                    }
                                }}
                            />
                        </Tooltip>
                        <p>{t("last")}</p>
                    </div>
                </div>
                <div className={wpStyles.settings}>
                    {waypointType !== "INVISIBLE" && (
                        <div className={wpStyles.settingCheckbox}>
                            <Tooltip title={hasOnlyGardenStories ? t("disabledIsInGarden") : t("isInGarden")}>
                                <span>
                                    <Checkbox
                                        checked={isInGarden}
                                        onChange={async (_, checked) => {
                                            const prev = isInGarden;
                                            setIsInGarden(checked);
                                            try {
                                                await saveWaypoint({ type: checked ? "GARDEN" : "FIXED" });
                                            } catch {
                                                setIsInGarden(prev);
                                                showNotification({ message: t("waypointSaveFailed"), type: "error" });
                                            }
                                        }}
                                        disabled={hasOnlyGardenStories}
                                    />
                                </span>
                            </Tooltip>
                            <p>{t("garden")}</p>
                        </div>
                    )}
                    <div className={wpStyles.settingCheckbox}>
                        <Tooltip title={t("isStraightLine")}>
                            <Checkbox
                                checked={isStraightLine}
                                onChange={async (_, checked) => {
                                    const prev = isStraightLine;
                                    setIsStraightLine(checked);
                                    try {
                                        await saveWaypoint({ isStraightLine: checked });
                                    } catch {
                                        setIsStraightLine(prev);
                                        showNotification({ message: t("waypointSaveFailed"), type: "error" });
                                    }
                                }}
                            />
                        </Tooltip>
                        <p>{t("straight")}</p>
                    </div>
                </div>
            </div>
            <div className={wpStyles.actions}>
                {waypointType !== "INVISIBLE" && (
                    <Tooltip title={t("editContent")} arrow>
                        <button
                            type="button"
                            className={wpStyles.iconBtn}
                            onClick={onClick}
                            aria-label={t("editContent")}
                        >
                            <EditSvg className={wpStyles.iconGlyph} />
                        </button>
                    </Tooltip>
                )}
                <Tooltip title={t("delete")} arrow>
                    <button
                        type="button"
                        className={`${wpStyles.iconBtn} ${wpStyles.iconBtnDanger}`}
                        aria-label={t("delete")}
                        onClick={() => {
                            if (!isLoading && window.confirm(t("deleteConfirmation"))) {
                                onDelete();
                            }
                        }}
                    >
                        <DeleteBinSvg className={wpStyles.iconGlyph} />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

export default EditableWaypointCard;
